"use client";

import {
  FaDatabase,
  FaEdit,
  FaPlus,
  FaTrash,
  FaSave,
  FaSpinner,
  FaMapMarkerAlt,
  FaHandshake,
  FaFileAlt,
} from "react-icons/fa";
import { INDIAN_STATES, BRAND_OPTIONS } from "./shared";
import { useState, useEffect } from "react";

const CodeDetailsSection = ({
  loading,
  userCodes,
  editingCode,
  editedCodeData,
  addingSiteToCode,
  newSiteForm,
  newCodeForm,
  validation,
  depots,
  copiedAddresses,
  showAddressDropdown,
  showNewSiteAddressDropdown,
  rateRequests = [], // <--- default empty array
  requestsLoading = false,
  // Handlers
  startEditingCode,
  cancelEditingCode,
  handleEditedCodeChange,
  saveEditedCode,
  toggleAddingSite,
  handleNewSiteChange,
  addNewSiteToCode,
  handleNewCodeChange,
  handleNewCodeSiteChange,
  handleSubmitNewCode,
  deleteCode,
  setAddingSiteToCode,
  setShowAddressDropdown,
  setShowNewSiteAddressDropdown,
  applyCopiedAddress,
  applyCopiedAddressForNewSite,
  clearCopiedAddress,
  clearAllCopiedAddresses,
}) => {
  // New state for layout
  const [viewMode, setViewMode] = useState("codes"); // "codes" or "requests"
  const [selectedItem, setSelectedItem] = useState(null); // selected code or request
  const [showNewCodeForm, setShowNewCodeForm] = useState(false);

  // Local error states for real-time validation
  const [newCodeErrors, setNewCodeErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [addSiteErrors, setAddSiteErrors] = useState({});

  // Validation function
  const validateField = (field, value, additionalData = {}) => {
    let error = "";
    const { brand } = additionalData;
    switch (field) {
      case "codeID":
        if (!value?.trim()) error = "Code ID is required";
        else if (value.length > 20) error = "Maximum 20 characters allowed";
        break;
      case "balance":
        if (value && (isNaN(Number(value)) || Number(value) < 0))
          error = "Balance must be a valid non-negative number";
        break;
      case "brand":
        if (!value) error = "Brand is required";
        break;
      case "transportation_type":
case "sites.transportation_type":
  if (!value) error = "Transportation Type is required";
  break;
      case "depoName":
        if (brand && !value) error = "Depot Name is required";
        break;
      case "depoAccount.AccountNumber":
      case "accountNumber":
        const acc = value;
        if (!acc?.trim()) error = "Account Number is required";
        else if (acc.length > 25) error = "Maximum 25 characters allowed";
        break;
      case "depoAccount.BeneficiaryName":
      case "beneficiaryName":
        const ben = value;
        if (!ben?.trim()) error = "Beneficiary Name is required";
        else if (!/^[a-zA-Z\s]+$/.test(ben.trim()))
          error = "Only letters and spaces are allowed";
        break;
      case "depoAccount.IFSC":
      case "ifsc":
        const ifsc = value;
        if (!ifsc?.trim()) error = "IFSC Code is required";
        else if (ifsc.length !== 11)
          error = "IFSC Code must be exactly 11 characters";
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase()))
          error = "Invalid IFSC format (e.g., SBIN0ABCDEF)";
        break;
      case "depoAccount.Branch":
      case "branch":
        if (!value?.trim()) error = "Bank & Branch is required";
        break;
      case "address":
      case "sites.address":
        if (!value?.trim()) error = "Address is required";
        break;
      case "state":
      case "sites.state":
        if (!value) error = "State is required";
        break;
      case "pincode":
      case "sites.pincode":
        if (value && !/^\d{6}$/.test(value))
          error = "Pincode must be exactly 6 digits";
        break;
    }
    return error;
  };

  // Auto-fill new code form when "Make New Code" from request
  const handleMakeNewCodeFromRequest = (request) => {
    handleNewCodeChange("brand", request.brand || "");
    handleNewCodeSiteChange("address", request.siteAddress?.address || "");
    handleNewCodeSiteChange("state", request.siteAddress?.state || "");
    handleNewCodeSiteChange("pincode", request.siteAddress?.pincode || "");
    setSelectedItem(null);
    setShowNewCodeForm(true);
    setViewMode("codes");
  };
  const renderCodeInfo = (code, isEditing) => {
    if (isEditing) {
      return (
        <div className="code-edit-form">
          <div className="edit-grid">
            <div className="form-group">
              <label className={editErrors.codeID ? "text-danger" : ""}>
                Code ID
              </label>
              <input
                type="text"
                value={editedCodeData.codeID || ""}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  handleEditedCodeChange("codeID", value);
                  const error = validateField("codeID", value);
                  setEditErrors((prev) => ({ ...prev, codeID: error }));
                }}
                className={`form-control ${editErrors.codeID ? "has-error" : ""}`}
                maxLength={20}
              />
              {editErrors.codeID && (
                <span className="error-text">{editErrors.codeID}</span>
              )}
            </div>
            <div className="form-group">
              <label className={editErrors.balance ? "text-danger" : ""}>
                Balance
              </label>
              <input
                type="text"
                value={editedCodeData.balance || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleEditedCodeChange("balance", value);
                  const error = validateField("balance", value);
                  setEditErrors((prev) => ({ ...prev, balance: error }));
                }}
                className={`form-control ${editErrors.balance ? "has-error" : ""}`}
              />
              {editErrors.balance && (
                <span className="error-text">{editErrors.balance}</span>
              )}
            </div>
            <div className="form-group">
              <label className={editErrors.brand ? "text-danger" : ""}>
                Brand <span className="required">*</span>
              </label>
              <select
                value={editedCodeData.brand || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleEditedCodeChange("brand", value);
                  const error = validateField("brand", value);
                  setEditErrors((prev) => ({ ...prev, brand: error }));
                  setEditErrors((prev) => ({ ...prev, depoName: "" }));
                }}
                className={`form-control ${editErrors.brand ? "has-error" : ""}`}
              >
                <option value="">--Select Brand--</option>
                {BRAND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {editErrors.brand && (
                <span className="error-text">{editErrors.brand}</span>
              )}
            </div>
            <div className="form-group">
              <label className={editErrors.depoName ? "text-danger" : ""}>
                Depot Name <span className="required">*</span>
              </label>
              <select
                value={editedCodeData.depoName || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleEditedCodeChange("depoName", value);
                  const error = validateField("depoName", value, {
                    brand: editedCodeData.brand,
                  });
                  setEditErrors((prev) => ({ ...prev, depoName: error }));
                }}
                className={`form-control ${editErrors.depoName ? "has-error" : ""}`}
              >
                <option value="">--Select Depot--</option>
                {depots
                  .filter((depot) => depot.brandName === editedCodeData.brand)
                  .map((depot) => (
                    <option key={depot.id} value={depot.depoName}>
                      {depot.depoName}
                    </option>
                  ))}
              </select>
              {editErrors.depoName && (
                <span className="error-text">{editErrors.depoName}</span>
              )}
            </div>
          </div>
          <div className="form-section">
            <h5>Depot Account Details</h5>
            <div className="edit-grid">
              <div className="form-group">
                <label
                  className={
                    editErrors["depoAccount.AccountNumber"] ? "text-danger" : ""
                  }
                >
                  Account Number <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.AccountNumber || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    handleEditedCodeChange("depoAccount.AccountNumber", value);
                    const error = validateField(
                      "depoAccount.AccountNumber",
                      value,
                    );
                    setEditErrors((prev) => ({
                      ...prev,
                      "depoAccount.AccountNumber": error,
                    }));
                  }}
                  className={`form-control ${editErrors["depoAccount.AccountNumber"] ? "has-error" : ""}`}
                  maxLength={25}
                />
                {editErrors["depoAccount.AccountNumber"] && (
                  <span className="error-text">
                    {editErrors["depoAccount.AccountNumber"]}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label
                  className={
                    editErrors["depoAccount.BeneficiaryName"]
                      ? "text-danger"
                      : ""
                  }
                >
                  Beneficiary Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.BeneficiaryName || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleEditedCodeChange(
                      "depoAccount.BeneficiaryName",
                      value,
                    );
                    const error = validateField(
                      "depoAccount.BeneficiaryName",
                      value,
                    );
                    setEditErrors((prev) => ({
                      ...prev,
                      "depoAccount.BeneficiaryName": error,
                    }));
                  }}
                  className={`form-control ${editErrors["depoAccount.BeneficiaryName"] ? "has-error" : ""}`}
                  maxLength={50}
                />
                {editErrors["depoAccount.BeneficiaryName"] && (
                  <span className="error-text">
                    {editErrors["depoAccount.BeneficiaryName"]}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label
                  className={
                    editErrors["depoAccount.IFSC"] ? "text-danger" : ""
                  }
                >
                  IFSC Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.IFSC || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    handleEditedCodeChange("depoAccount.IFSC", value);
                    const error = validateField("depoAccount.IFSC", value);
                    setEditErrors((prev) => ({
                      ...prev,
                      "depoAccount.IFSC": error,
                    }));
                  }}
                  className={`form-control ${editErrors["depoAccount.IFSC"] ? "has-error" : ""}`}
                  maxLength={11}
                />
                {editErrors["depoAccount.IFSC"] && (
                  <span className="error-text">
                    {editErrors["depoAccount.IFSC"]}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label
                  className={
                    editErrors["depoAccount.Branch"] ? "text-danger" : ""
                  }
                >
                  Bank & Branch <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.Branch || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleEditedCodeChange("depoAccount.Branch", value);
                    const error = validateField("depoAccount.Branch", value);
                    setEditErrors((prev) => ({
                      ...prev,
                      "depoAccount.Branch": error,
                    }));
                  }}
                  className={`form-control ${editErrors["depoAccount.Branch"] ? "has-error" : ""}`}
                />
                {editErrors["depoAccount.Branch"] && (
                  <span className="error-text">
                    {editErrors["depoAccount.Branch"]}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={cancelEditingCode}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={saveEditedCode}
              disabled={Object.values(editErrors).some(Boolean)}
            >
              <FaSave /> Save Changes
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="info-grid">
          <div className="info-row">
            <label>Code ID:</label>
            <span>{code.codeID || "N/A"}</span>
          </div>
          <div className="info-row">
            <label>Balance:</label>
            <span>{code.balance || "To be updated"}</span>
          </div>
          <div className="info-row">
            <label>Brand:</label>
            <span className="brand-badge">{code.brand || "N/A"}</span>
          </div>

          <div className="info-row">
            <label>Depot Name:</label>
            <span>{code.depoName || "N/A"}</span>
          </div>
        </div>
        <div className="subsection">
          <h5>Depot Account Details</h5>
          {code.depoAccount ? (
            <div className="info-grid">
              <div className="info-row">
                <label>Account Number:</label>
                <span>{code.depoAccount.AccountNumber || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>Beneficiary Name:</label>
                <span>{code.depoAccount.BeneficiaryName || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>IFSC Code:</label>
                <span>{code.depoAccount.IFSC || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>Bank & Branch:</label>
                <span>{code.depoAccount.Branch || "N/A"}</span>
              </div>
            </div>
          ) : (
            <p className="no-data">No depot account details available</p>
          )}
        </div>
        <div className="subsection">
          <h5>Site Addresses</h5>
          {code.sites && Object.keys(code.sites).length > 0 ? (
            <div className="sites-list">
              {Object.entries(code.sites).map(([siteKey, siteData]) => (
                <div key={siteKey} className="site-item">
                  <div className="site-badge">Site {siteKey}</div>
                  <div className="site-details">
                    <div className="info-row">
                      <label>Address:</label>
                      <span>{siteData.address || "N/A"}</span>
                    </div>
                    <div className="info-row">
                      <label>State:</label>
                      <span>{siteData.state || "N/A"}</span>
                    </div>
                    <div className="info-row">
                      <label>Pincode:</label>
                      <span>{siteData.pincode || "N/A"}</span>
                    </div>
                    <div className="info-row">
                      <label>Transportation Type:</label>
                      <span>{siteData.transportation_type || "N/A"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No site addresses available</p>
          )}
        </div>
      </>
    );
  };
  const renderAddSiteForm = (code) => {
    if (addingSiteToCode !== code.id) return null;
    return (
      <div className="add-site-form">
        <h5>
          <FaPlus /> Add New Site
        </h5>
        {copiedAddresses.length > 0 && (
          <div className="copied-addresses-section">
            <button
              type="button"
              className="btn btn-info"
              onClick={() =>
                setShowNewSiteAddressDropdown(!showNewSiteAddressDropdown)
              }
            >
              <FaMapMarkerAlt /> Use Copied Address ({copiedAddresses.length})
            </button>
            {showNewSiteAddressDropdown && (
              <div className="addresses-dropdown">
                <div className="dropdown-header">
                  <span>Select from copied addresses:</span>
                  <button onClick={() => setShowNewSiteAddressDropdown(false)}>
                    ×
                  </button>
                </div>
                <div className="addresses-list">
                  {copiedAddresses.map((addr, index) => (
                    <div
                      key={index}
                      className="address-item"
                      onClick={() => applyCopiedAddressForNewSite(addr)}
                    >
                      <div className="address-content">
                        <strong>{addr.address}</strong>
                        <span>
                          {addr.state}
                          {addr.pincode && `, ${addr.pincode}`}
                        </span>
                        <div className="address-meta">
                          {addr.companyName && (
                            <span className="tag company">
                              {addr.companyName}
                            </span>
                          )}
                          {addr.brand && (
                            <span className="tag brand">{addr.brand}</span>
                          )}
                        </div>
                      </div>
                      <button
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearCopiedAddress(index);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="site-form-fields">
          <div className="form-group">
            <label className={addSiteErrors.address ? "text-danger" : ""}>
              Address <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newSiteForm.address}
              onChange={(e) => {
                const value = e.target.value;
                handleNewSiteChange("address", value);
                const error = validateField("address", value);
                setAddSiteErrors((prev) => ({ ...prev, address: error }));
              }}
              placeholder="Enter address"
              className={`form-control ${addSiteErrors.address ? "has-error" : ""}`}
            />
            {addSiteErrors.address && (
              <span className="error-text">{addSiteErrors.address}</span>
            )}
          </div>
          <div className="form-group">
            <label className={addSiteErrors.state ? "text-danger" : ""}>
              State <span className="required">*</span>
            </label>
            <select
              value={newSiteForm.state}
              onChange={(e) => {
                const value = e.target.value;
                handleNewSiteChange("state", value);
                const error = validateField("state", value);
                setAddSiteErrors((prev) => ({ ...prev, state: error }));
              }}
              className={`form-control ${addSiteErrors.state ? "has-error" : ""}`}
            >
              <option value="">--Select State--</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {addSiteErrors.state && (
              <span className="error-text">{addSiteErrors.state}</span>
            )}
          </div>
          <div className="form-group">
            <label className={addSiteErrors.pincode ? "text-danger" : ""}>
              Pincode (Optional)
            </label>
            <input
              type="text"
              value={newSiteForm.pincode}
              onChange={(e) => {
                const value = e.target.value;
                handleNewSiteChange("pincode", value);
                const error = validateField("pincode", value);
                setAddSiteErrors((prev) => ({ ...prev, pincode: error }));
              }}
              placeholder="6-digit pincode"
              className={`form-control ${addSiteErrors.pincode ? "has-error" : ""}`}
              maxLength={6}
            />
            {addSiteErrors.pincode && (
              <span className="error-text">{addSiteErrors.pincode}</span>
            )}
          </div>
          <div className="form-group">
            <label
              className={addSiteErrors.transportation_type ? "text-danger" : ""}
            >
              Transportation Type <span className="required">*</span>
            </label>

            <select
              value={newSiteForm.transportation_type || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleNewSiteChange("transportation_type", value);

                const error = validateField("transportation_type", value);

                setAddSiteErrors((prev) => ({
                  ...prev,
                  transportation_type: error,
                }));
              }}
              className={`form-control ${
                addSiteErrors.transportation_type ? "has-error" : ""
              }`}
            >
              <option value="">--Select Type--</option>
              <option value="Ex">Ex</option>
              <option value="FOR">FOR</option>
            </select>

            {addSiteErrors.transportation_type && (
              <span className="error-text">
                {addSiteErrors.transportation_type}
              </span>
            )}
          </div>
        </div>
        <div className="form-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setAddingSiteToCode(null)}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => addNewSiteToCode(code.id)}
            disabled={Object.values(addSiteErrors).some(Boolean)}
          >
            Add Site
          </button>
        </div>
      </div>
    );
  };
  const renderNewCodeForm = () => (
    <div className="new-code-form-section">
      <h3>
        <FaPlus /> Add New Code
      </h3>
      {copiedAddresses.length > 0 && (
        <div className="copied-addresses-info">
          <FaMapMarkerAlt />
          <span>Copied Site Addresses ({copiedAddresses.length})</span>
          <button onClick={clearAllCopiedAddresses} className="clear-all-btn">
            Clear All
          </button>
        </div>
      )}
      <div className="new-code-form">
        <div className="form-group">
          <label className={newCodeErrors.codeID ? "text-danger" : ""}>
            Code ID <span className="required">*</span>
          </label>
          <input
            type="text"
            value={newCodeForm.codeID}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              handleNewCodeChange("codeID", value);
              const error = validateField("codeID", value);
              setNewCodeErrors((prev) => ({ ...prev, codeID: error }));
            }}
            placeholder="Max 20 chars, uppercase & digits"
            className={`form-control ${newCodeErrors.codeID ? "has-error" : ""}`}
            maxLength={20}
          />
          {newCodeErrors.codeID && (
            <span className="error-text">{newCodeErrors.codeID}</span>
          )}
        </div>
        <div className="form-group">
          <label className={newCodeErrors.brand ? "text-danger" : ""}>
            Brand <span className="required">*</span>
          </label>
          <select
            value={newCodeForm.brand}
            onChange={(e) => {
              const value = e.target.value;
              handleNewCodeChange("brand", value);
              const error = validateField("brand", value);
              setNewCodeErrors((prev) => ({ ...prev, brand: error }));
              setNewCodeErrors((prev) => ({ ...prev, depoName: "" }));
            }}
            className={`form-control ${newCodeErrors.brand ? "has-error" : ""}`}
          >
            <option value="">--Select Brand--</option>
            {BRAND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {newCodeErrors.brand && (
            <span className="error-text">{newCodeErrors.brand}</span>
          )}
        </div>

        {newCodeForm.brand &&
          depots.filter((d) => d.brandName === newCodeForm.brand).length >
            0 && (
            <div className="form-group">
              <label className={newCodeErrors.depoName ? "text-danger" : ""}>
                Depot Name <span className="required">*</span>
              </label>
              <select
                value={newCodeForm.depoName}
                onChange={(e) => {
                  const value = e.target.value;
                  handleNewCodeChange("depoName", value);
                  const error = validateField("depoName", value, {
                    brand: newCodeForm.brand,
                  });
                  setNewCodeErrors((prev) => ({ ...prev, depoName: error }));
                }}
                className={`form-control ${newCodeErrors.depoName ? "has-error" : ""}`}
              >
                <option value="">--Select Depot--</option>
                {depots
                  .filter((depot) => depot.brandName === newCodeForm.brand)
                  .map((depot) => (
                    <option key={depot.id} value={depot.depoName}>
                      {depot.depoName}
                    </option>
                  ))}
              </select>
              {newCodeErrors.depoName && (
                <span className="error-text">{newCodeErrors.depoName}</span>
              )}
            </div>
          )}
        <div className="form-group">
          <label>Balance</label>
          <input
            type="number"
            value={newCodeForm.balance || ""}
            onChange={(e) => handleNewCodeChange("balance", e.target.value)}
            placeholder="Enter balance amount"
            className="form-control"
            disabled
          />
        </div>
        <div className="form-section">
          <h4>Depot Account Details</h4>
          <div className="form-group">
            <label
              className={
                newCodeErrors["depoAccount.AccountNumber"] ? "text-danger" : ""
              }
            >
              Account Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.AccountNumber}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                handleNewCodeChange("depoAccount.AccountNumber", value);
                const error = validateField("depoAccount.AccountNumber", value);
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "depoAccount.AccountNumber": error,
                }));
              }}
              placeholder="Max 25 chars"
              className={`form-control ${newCodeErrors["depoAccount.AccountNumber"] ? "has-error" : ""}`}
              maxLength={25}
            />
            {newCodeErrors["depoAccount.AccountNumber"] && (
              <span className="error-text">
                {newCodeErrors["depoAccount.AccountNumber"]}
              </span>
            )}
          </div>
          <div className="form-group">
            <label
              className={
                newCodeErrors["depoAccount.BeneficiaryName"]
                  ? "text-danger"
                  : ""
              }
            >
              Beneficiary Name <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.BeneficiaryName}
              onChange={(e) => {
                const value = e.target.value;
                handleNewCodeChange("depoAccount.BeneficiaryName", value);
                const error = validateField(
                  "depoAccount.BeneficiaryName",
                  value,
                );
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "depoAccount.BeneficiaryName": error,
                }));
              }}
              placeholder="Letters only"
              className={`form-control ${newCodeErrors["depoAccount.BeneficiaryName"] ? "has-error" : ""}`}
              maxLength={50}
            />
            {newCodeErrors["depoAccount.BeneficiaryName"] && (
              <span className="error-text">
                {newCodeErrors["depoAccount.BeneficiaryName"]}
              </span>
            )}
          </div>
          <div className="form-group">
            <label
              className={newCodeErrors["depoAccount.IFSC"] ? "text-danger" : ""}
            >
              IFSC Code <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.IFSC}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                handleNewCodeChange("depoAccount.IFSC", value);
                const error = validateField("depoAccount.IFSC", value);
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "depoAccount.IFSC": error,
                }));
              }}
              placeholder="AAAA0XXXXXX"
              className={`form-control ${newCodeErrors["depoAccount.IFSC"] ? "has-error" : ""}`}
              maxLength={11}
            />
            {newCodeErrors["depoAccount.IFSC"] && (
              <span className="error-text">
                {newCodeErrors["depoAccount.IFSC"]}
              </span>
            )}
          </div>
          <div className="form-group">
            <label
              className={
                newCodeErrors["depoAccount.Branch"] ? "text-danger" : ""
              }
            >
              Bank & Branch <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.Branch}
              onChange={(e) => {
                const value = e.target.value;
                handleNewCodeChange("depoAccount.Branch", value);
                const error = validateField("depoAccount.Branch", value);
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "depoAccount.Branch": error,
                }));
              }}
              placeholder="Bank name and branch"
              className={`form-control ${newCodeErrors["depoAccount.Branch"] ? "has-error" : ""}`}
            />
            {newCodeErrors["depoAccount.Branch"] && (
              <span className="error-text">
                {newCodeErrors["depoAccount.Branch"]}
              </span>
            )}
          </div>
        </div>
        <div className="form-section">
          <h4>Site Address</h4>
          {copiedAddresses.length > 0 && (
            <div className="copied-addresses-section">
              <button
                type="button"
                className="btn btn-info"
                onClick={() => setShowAddressDropdown(!showAddressDropdown)}
              >
                <FaMapMarkerAlt /> Use Copied Address ({copiedAddresses.length})
              </button>
              {showAddressDropdown && (
                <div className="addresses-dropdown">
                  <div className="dropdown-header">
                    <span>Select from copied addresses:</span>
                    <button onClick={() => setShowAddressDropdown(false)}>
                      ×
                    </button>
                  </div>
                  <div className="addresses-list">
                    {copiedAddresses.map((addr, index) => (
                      <div
                        key={index}
                        className="address-item"
                        onClick={() => applyCopiedAddress(addr)}
                      >
                        <div className="address-content">
                          <strong>{addr.address}</strong>
                          <span>
                            {addr.state}
                            {addr.pincode && `, ${addr.pincode}`}
                          </span>
                          <div className="address-meta">
                            {addr.companyName && (
                              <span className="tag company">
                                {addr.companyName}
                              </span>
                            )}
                            {addr.brand && (
                              <span className="tag brand">{addr.brand}</span>
                            )}
                          </div>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearCopiedAddress(index);
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="form-group">
            <label
              className={newCodeErrors["sites.address"] ? "text-danger" : ""}
            >
              Address <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.sites[1].address}
              onChange={(e) => {
                const value = e.target.value;
                handleNewCodeSiteChange("address", value);
                const error = validateField("sites.address", value);
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "sites.address": error,
                }));
              }}
              placeholder="Enter address"
              className={`form-control ${newCodeErrors["sites.address"] ? "has-error" : ""}`}
            />
            {newCodeErrors["sites.address"] && (
              <span className="error-text">
                {newCodeErrors["sites.address"]}
              </span>
            )}
          </div>
          <div className="form-group">
            <label
              className={newCodeErrors["sites.state"] ? "text-danger" : ""}
            >
              State <span className="required">*</span>
            </label>
            <select
              value={newCodeForm.sites[1].state}
              onChange={(e) => {
                const value = e.target.value;
                handleNewCodeSiteChange("state", value);
                const error = validateField("sites.state", value);
                setNewCodeErrors((prev) => ({ ...prev, "sites.state": error }));
              }}
              className={`form-control ${newCodeErrors["sites.state"] ? "has-error" : ""}`}
            >
              <option value="">--Select State--</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {newCodeErrors["sites.state"] && (
              <span className="error-text">{newCodeErrors["sites.state"]}</span>
            )}
          </div>
          <div className="form-group">
            <label
              className={newCodeErrors["sites.pincode"] ? "text-danger" : ""}
            >
              Pincode (Optional)
            </label>
            <input
              type="text"
              value={newCodeForm.sites[1].pincode}
              onChange={(e) => {
                const value = e.target.value;
                handleNewCodeSiteChange("pincode", value);
                const error = validateField("sites.pincode", value);
                setNewCodeErrors((prev) => ({
                  ...prev,
                  "sites.pincode": error,
                }));
              }}
              placeholder="6-digit pincode"
              className={`form-control ${newCodeErrors["sites.pincode"] ? "has-error" : ""}`}
              maxLength={6}
            />
            {newCodeErrors["sites.pincode"] && (
              <span className="error-text">
                {newCodeErrors["sites.pincode"]}
              </span>
            )}
          </div>
          <div className="form-group">
            <label
              className={
                newCodeErrors["sites.transportation_type"] ? "text-danger" : ""
              }
            >
              Transportation Type <span className="required">*</span>
            </label>

            <select
              value={newCodeForm.sites[1].transportation_type || ""}
              onChange={(e) => {
                const value = e.target.value;

                handleNewCodeSiteChange("transportation_type", value);

                const error = validateField("sites.transportation_type", value);

                setNewCodeErrors((prev) => ({
                  ...prev,
                  "sites.transportation_type": error,
                }));
              }}
              className={`form-control ${
                newCodeErrors["sites.transportation_type"] ? "has-error" : ""
              }`}
            >
              <option value="">--Select Type--</option>
              <option value="Ex">Ex</option>
              <option value="FOR">FOR</option>
            </select>

            {newCodeErrors["sites.transportation_type"] && (
              <span className="error-text">
                {newCodeErrors["sites.transportation_type"]}
              </span>
            )}
          </div>
        </div>
        <div className="form-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSubmitNewCode}
            disabled={loading || Object.values(newCodeErrors).some(Boolean)}
          >
            {loading ? (
              <>
                <FaSpinner className="spinner" /> Submitting...
              </>
            ) : (
              <>
                <FaSave /> Add New Code
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="code-details-tab new-layout">
      {/* Simplified Top Bar */}
      <div className="top-bar">
        <div className="top-title">
          <FaHandshake className="icon" />
          <h2>Manage User Codes</h2>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setShowNewCodeForm(true);
            setSelectedItem(null);
          }}
        >
          <FaPlus /> Add New Code
        </button>
      </div>
      <div className="main-container">
        {/* Left Sidebar */}
        <div className="left-sidebar">
          <div className="toggle-bar">
            <button
              className={viewMode === "codes" ? "active" : ""}
              onClick={() => setViewMode("codes")}
            >
              User Codes ({userCodes.length})
            </button>
            <button
              className={viewMode === "requests" ? "active" : ""}
              onClick={() => setViewMode("requests")}
            >
              Code Requests ({rateRequests.length})
            </button>
          </div>
          <div className="list">
            {viewMode === "codes" ? (
              loading ? (
                <div className="loading-state">
                  <FaSpinner className="spinner" /> Loading codes...
                </div>
              ) : userCodes.length === 0 ? (
                <p className="empty">No codes found</p>
              ) : (
                userCodes.map((code) => (
                  <div
                    key={code.id}
                    className={`list-item ${
                      selectedItem?.id === code.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedItem(code)}
                  >
                    <div className="item-title">{code.codeID}</div>
                    <div className="item-subtitle">{code.brand}</div>
                  </div>
                ))
              )
            ) : requestsLoading ? (
              <div className="loading-state">
                <FaSpinner className="spinner" /> Loading requests...
              </div>
            ) : rateRequests.length === 0 ? (
              <p className="empty">No closed code requests</p>
            ) : (
              rateRequests.map((req) => (
                <div
                  key={req.id}
                  className="list-item request"
                  onClick={() => setSelectedItem(req)}
                >
                  <div className="item-title">{req.brand}</div>
                  {req.timestamp && (
                    <div className="item-timestamp">
                      {new Date(req.timestamp).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        {/* Right Detail Window */}
        <div className="right-detail">
          {selectedItem ? (
            selectedItem.siteAddress ? (
              // Selected Rate Request
              <div className="request-detail">
                <div className="detail-header">
                  <h3>
                    <FaFileAlt /> Closed Rate Request
                  </h3>
                  <div className="actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleMakeNewCodeFromRequest(selectedItem)}
                    >
                      <FaPlus /> Make New Code
                    </button>
                  </div>
                </div>
                <div className="info-grid">
                  <div className="info-row">
                    <label>Site Address</label>
                    <span>
                      {selectedItem.siteAddress?.address},{" "}
                      {selectedItem.siteAddress?.state}{" "}
                      {selectedItem.siteAddress?.pincode}
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Brand</label>
                    <span>{selectedItem.brand}</span>
                  </div>
                  <div className="info-row">
                    <label>Status</label>
                    <span className={`status ${selectedItem.status}`}>
                      {selectedItem.status}
                    </span>
                  </div>
                  {selectedItem.timestamp && (
                    <div className="info-row">
                      <label>Timestamp</label>
                      <span>
                        {new Date(selectedItem.timestamp).toLocaleDateString(
                          "en-GB",
                          {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}{" "}
                        {new Date(selectedItem.timestamp).toLocaleTimeString(
                          "en-GB",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Selected User Code
              <div className="code-detail">
                <div className="detail-header">
                  <h3>Code: {selectedItem.codeID}</h3>
                  <div className="actions">
                    <button
                      className="icon-btn"
                      onClick={() => startEditingCode(selectedItem)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => toggleAddingSite(selectedItem.id)}
                      title="Add Site"
                    >
                      <FaPlus />
                    </button>
                    <button
                      className="icon-btn danger"
                      onClick={() => deleteCode(selectedItem.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                {renderCodeInfo(selectedItem, editingCode === selectedItem.id)}
                {renderAddSiteForm(selectedItem)}
              </div>
            )
          ) : showNewCodeForm ? (
            renderNewCodeForm()
          ) : (
            <div className="empty-detail">
              <FaDatabase className="big-icon" />
              <p>Select a code or request from the left to view details</p>
              <p>Or click "Add New Code" to create one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default CodeDetailsSection;
